/**
 * Helpers de date, purs et sans dépendance.
 *
 * ⚠️ Dossier `lib/` ajouté à l'arborescence du §3 : il n'y avait pas d'endroit
 * pour une fonction pure sans React ni I/O, et l'écran « Aujourd'hui » comme le
 * volet planning des Réglages en ont besoin. Volontairement minuscule — aucune
 * dépendance n'a été ajoutée pour ça (budget 0 €, stack légère).
 *
 * ⚠️ **La date de référence fait autorité côté serveur**, pas ici. La journée
 * courante vient de `GET /api/days/today`, bornée par `TZ` (§9). Les fonctions
 * ci-dessous ne servent qu'à naviguer et à afficher une date déjà connue : elles
 * manipulent une chaîne `YYYY-MM-DD` comme un jour calendaire, en UTC, pour que
 * le fuseau du navigateur n'introduise jamais un décalage d'un jour.
 */

/** Décale une date `YYYY-MM-DD` d'un nombre entier de jours. */
export function shiftDate(date: string, days: number): string {
  const shifted = new Date(`${date}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

/** Date du jour selon le navigateur — utilisée seulement pour un repère visuel. */
export function browserToday(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/**
 * « Lundi 17 août » — l'année n'apparaît que si elle diffère de l'année en cours.
 *
 * ⚠️ La capitalisation est faite ici, sur la **première lettre seulement**. Le
 * CSS `text-transform: capitalize` capitalise chaque mot et produirait
 * « Lundi 17 Août » : en français, les noms de mois sont en minuscules.
 */
export function formatDayLabel(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  const sameYear = parsed.getUTCFullYear() === new Date().getFullYear();
  const label = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  }).format(parsed);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Libellés du planning hebdomadaire — **0 = lundi**, comme la colonne `weekday` (§4.4). */
export const WEEKDAY_LABELS = [
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi',
  'Dimanche',
] as const;

/**
 * Identifiant de mutation tiré par le client (règle non négociable n°9).
 *
 * `crypto.randomUUID` exige un contexte sécurisé — c'est toujours le cas ici
 * (HTTPS en production, `localhost` en développement), mais le repli garantit
 * qu'une saisie ne se perd jamais pour cette raison.
 */
export function newClientId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
